import { useEffect } from 'react'
import { useStore } from 'store'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'

import usePrevious from 'hooks/usePrevious'

import AddDemandScreen from 'pages/buyer/AddDemandScreen'
import AdditionalInformationScreen from 'pages/seller/AdditionalInformation'
import AddBuiltOfferScreen from 'pages/seller/AddBuiltOfferScreen'
import AddPlannedOfferScreen from 'pages/seller/AddPlannedOfferScreen'
import BuyerDetailsScreen from 'pages/buyer/DetailsScreen'
import EmailVerificationScreen from 'pages/EmailVerificationScreen'
import FileUploadConfirmation from 'pages/FileUploadConfirmation'
import FileUploadFormatChoice from 'pages/FileUploadFormatChoice'
import FileUploadSuccessScreen from 'pages/FileUploadSuccessScreen'
import MatchesScreen from 'pages/matches/MatchesScreen'
import RegistrationScreen from 'pages/RegistrationScreen'
import SearchMatchesScreen from 'pages/matches/SearchMatchesScreen'
import SearchMatchingOffers from 'pages/matches/SearchMatchingOffers'
import SearchOffersScreen from 'pages/matches/SearchOffersScreen'
import SearchScreen from 'pages/SearchScreen'
import SellerDetailsScreen from 'pages/seller/DetailsScreen'
import SuccessScreen from 'pages/SuccessScreen'

// Client panel
import BuyerDetailsClientScreen from 'pages/client-panel/buyer/DetailsScreen'
import BuyerDemandDetailsClientScreen from 'pages/client-panel/buyer/DemandDetailsScreen'
import BuyerMatchDetailsClientScreen from 'pages/client-panel/buyer/MatchDetailsScreen'
import ClientMatchesScreen from 'pages/client-panel/buyer/MatchesScreen'
import ConfirmationClientScreen from 'pages/client-panel/ConfirmationScreen'
import SellerDetailsClientScreen from 'pages/client-panel/seller/DetailsScreen'
import SellerOfferDetailsClientScreen from 'pages/client-panel/seller/OfferDetailsScreen'

import ProtectedRoute from 'components/ProtectedRoute'

import { basePaths, clientPanelPaths, clientPaths, paths } from 'paths'

export default function AppRoutes() {
  const { currentLoginUserClientPanel } = useStore()
  const navigate = useNavigate()
  const previousClientType = usePrevious(currentLoginUserClientPanel?.type)

  useEffect(() => {
    const isClientIndexPage = [basePaths.client_panel, `${basePaths.client_panel}/`].includes(
      window.location.pathname
    )

    if (isClientIndexPage && currentLoginUserClientPanel?.type !== previousClientType) {
      if (currentLoginUserClientPanel?.type === 'SELLER') {
        navigate(clientPanelPaths.offer_data)
      }

      if (currentLoginUserClientPanel?.type === 'BUYER') {
        navigate(clientPanelPaths.match_data)
      }
    }
  }, [currentLoginUserClientPanel?.type, previousClientType, navigate])

  return (
    <Routes>
      <Route path={basePaths.admin_panel}>
        <Route index element={<RegistrationScreen />} />
        <Route path={paths.registration} element={<RegistrationScreen />} />

        <Route path={clientPaths.seller}>
          <Route index element={<Navigate to={paths.registration} replace />} />
          <Route path={paths.additional_information} element={<AdditionalInformationScreen />} />
          <Route path={paths.add_offer} element={<AddBuiltOfferScreen />} />
          <Route path={paths.add_planned_offer} element={<AddPlannedOfferScreen />} />
          <Route path={paths.search}>
            <Route index element={<SearchScreen clientType="seller" />} />
            <Route path=":sellerId">
              <Route index element={<SellerDetailsScreen />} />
              <Route path={`${paths.file_upload}/:offerId`}>
                <Route
                  path={paths.format_choice}
                  element={<FileUploadFormatChoice clientType="seller" resourceType="offer" />}
                />
                <Route
                  path={paths.confirmation}
                  element={<FileUploadConfirmation clientType="seller" resourceType="offer" />}
                />
                <Route
                  path={paths.success}
                  element={<FileUploadSuccessScreen clientType="seller" />}
                />
              </Route>
            </Route>
            <Route path={paths.matching_offers} element={<SearchMatchingOffers />} />
          </Route>
        </Route>

        <Route path={clientPaths.buyer}>
          <Route index element={<Navigate to={paths.registration} replace />} />
          <Route path={paths.add_demand} element={<AddDemandScreen />} />
          <Route path={paths.search}>
            <Route index element={<SearchScreen clientType="buyer" />} />
            <Route path=":buyerId">
              <Route index element={<BuyerDetailsScreen />} />
              <Route path={`${paths.matching}/:demandId`} element={<SearchMatchingOffers />} />
              <Route path={`${paths.active_offers}/:demandId`} element={<SearchOffersScreen />} />
              <Route path={`${paths.file_upload}/:demandId`}>
                <Route
                  path={paths.format_choice}
                  element={<FileUploadFormatChoice clientType="buyer" resourceType="demand" />}
                />
                <Route
                  path={paths.confirmation}
                  element={<FileUploadConfirmation clientType="buyer" resourceType="demand" />}
                />
                <Route
                  path={paths.success}
                  element={<FileUploadSuccessScreen clientType="buyer" />}
                />
              </Route>
            </Route>
          </Route>
        </Route>

        <Route path={`${paths.matches}/${paths.search}`} element={<SearchMatchesScreen />} />
        <Route path={`${paths.matches}/:id`} element={<MatchesScreen useMatchId />} />
        <Route path={paths.registration_success} element={<SuccessScreen />} />
      </Route>

      <Route path={basePaths.client_panel}>
        <Route
          path={paths.personal_data}
          element={
            <>
              <ProtectedRoute allowedFor="SELLER">
                <SellerDetailsClientScreen />
              </ProtectedRoute>

              <ProtectedRoute allowedFor="BUYER">
                <BuyerDetailsClientScreen />
              </ProtectedRoute>
            </>
          }
        />
        <Route
          path={paths.offer_data}
          element={
            <ProtectedRoute allowedFor="SELLER">
              <SellerOfferDetailsClientScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.add_offer}
          element={
            <ProtectedRoute allowedFor="SELLER">
              <AddBuiltOfferScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.additional_information}
          element={
            <ProtectedRoute allowedFor="SELLER">
              <AdditionalInformationScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.add_planned_offer}
          element={
            <ProtectedRoute allowedFor="SELLER">
              <AddPlannedOfferScreen />
            </ProtectedRoute>
          }
        />

        <Route
          path={paths.match_data}
          element={
            <ProtectedRoute allowedFor="BUYER">
              <BuyerMatchDetailsClientScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.demand_data}
          element={
            <ProtectedRoute allowedFor="BUYER">
              <BuyerDemandDetailsClientScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.add_demand}
          element={
            <ProtectedRoute allowedFor="BUYER">
              <AddDemandScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={`${paths.matches}/:id`}
          element={
            <ProtectedRoute allowedFor="BUYER">
              <ClientMatchesScreen />
            </ProtectedRoute>
          }
        />
        <Route path={paths.confirmation} element={<ConfirmationClientScreen />} />
      </Route>

      <Route path={`${paths.offered_matches}/:id`} element={<MatchesScreen useDiffId />} />
      <Route path={`${paths.email_verification}/:id`} element={<EmailVerificationScreen />} />
      <Route path={paths.registration} element={<RegistrationScreen />} />
      <Route path={paths.registration_success} element={<SuccessScreen />} />

      {/* No routes matched cases: */}
      <Route
        path={`${basePaths.admin_panel}/*`}
        element={<Navigate to={basePaths.admin_panel} />}
      />

      <Route path="*" element={<Navigate to={basePaths.client_panel} />} />
    </Routes>
  )
}
